import { useState, useEffect, useCallback } from 'react';

/**
 * ============================================
 * Network Status Hook
 * ============================================
 *
 * Monitors browser network connectivity using native APIs.
 * Uses navigator.onLine + online/offline events.
 */

export interface UseNetworkStatusOptions {
  /** Callback when network reconnects */
  onReconnect?: () => void;
  /** Callback when network disconnects */
  onDisconnect?: () => void;
}

export interface UseNetworkStatusReturn {
  /** Whether the browser reports being online */
  isOnline: boolean;
  /** Manually refresh network status */
  refresh: () => void;
}

export function useNetworkStatus(options: UseNetworkStatusOptions = {}): UseNetworkStatusReturn {
  const { onReconnect, onDisconnect } = options;

  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  const refresh = useCallback(() => {
    if (typeof navigator !== 'undefined') {
      setIsOnline(navigator.onLine);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      setIsOnline(true);
      onReconnect?.();
    };

    const handleOffline = () => {
      setIsOnline(false);
      onDisconnect?.();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [onReconnect, onDisconnect]);

  return { isOnline, refresh };
}

export default useNetworkStatus;
